import { chain, filter, isEmpty } from 'lodash';

/**
 * Filters choice sets to only include leaf choices — choices that have no child
 * choice sets of their own. In the choice set tree, a leaf choice sits at the
 * bottom with no further nesting beneath it.
 *
 * A choice with child choice sets would introduce a third level of depth
 * (item → choice set → choice → choice set), which exceeds the supported
 * max depth of 2 and is considered invalid.
 *
 * Choice sets where all choices are invalid are removed entirely.
 */
export default function filterChoiceSetsByLeafChoices(
  choiceSets: ValidatedChoiceSet[],
): ValidatedChoiceSet[] {
  return chain(choiceSets)
    .map(choiceSet => ({
      ...choiceSet,
      choices: filter(choiceSet.choices, byResolvableChoicesOnly),
    }))
    .filter(byNonEmptyChoiceSet)
    .value();
}

const byResolvableChoicesOnly = (choice: ChoiceWithQuantity): boolean => {
  const hasRequiredNestedChoiceSet =
    'choiceSets' in choice &&
    !isEmpty(
      filter((choice as NestedChoiceWithQuantity).choiceSets, {
        required: true,
      }),
    );
  return !hasRequiredNestedChoiceSet;
};

const byNonEmptyChoiceSet = (choiceSet: ChoiceSetWithQuantity) =>
  !isEmpty(choiceSet.choices);
